import { supabase } from "@/integrations/supabase/client";
import type { AllEventType } from "./activityEvents";

interface LogParams {
  event: AllEventType;
  userId?: string;
  resourceType?: string;
  resourceId?: string;
  metadata?: Record<string, any>;
  description?: string;
}

/**
 * Centralized Activity Logger Service
 * Handles both authenticated and unauthenticated logging
 */
class ActivityLoggerService {
  /**
   * Log any activity event
   * Automatically detects if user is authenticated and routes to appropriate endpoint
   */
  async log(params: LogParams): Promise<void> {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const { event, userId, resourceType, resourceId, metadata, description } = params;

      const logData = {
        actionType: event.type,
        actionCategory: event.category,
        description: description || event.description || '',
        userId: userId || null,
        resourceType: resourceType || null,
        resourceId: resourceId || null,
        metadata: metadata || null,
      };

      // If event requires auth but no session, log warning and skip
      if (event.requiresAuth && !session) {
        console.warn(`Cannot log ${event.type}: requires authentication but no session found`);
        return;
      }

      // Route to appropriate endpoint based on authentication status
      if (session) {
        // Authenticated - use existing log-activity endpoint
        await supabase.functions.invoke('log-activity', {
          body: {
            ...logData,
            actor_id: session.user.id,
          },
        });
      } else {
        // Unauthenticated - use public logging endpoint
        await supabase.functions.invoke('log-public-activity', {
          body: logData,
        });
      }
    } catch (error) {
      console.error('Failed to log activity:', error);
    }
  }

  /**
   * Log authentication events
   */
  async logAuth(
    event: AllEventType,
    metadata?: Record<string, any>,
    description?: string
  ): Promise<void> {
    return this.log({ event, metadata, description });
  }

  /**
   * Log security events
   */
  async logSecurity(
    event: AllEventType,
    metadata?: Record<string, any>,
    description?: string
  ): Promise<void> {
    return this.log({ event, metadata, description });
  }

  /**
   * Log data modification events
   */
  async logData(
    event: AllEventType,
    resourceType: string,
    resourceId: string,
    userId?: string,
    metadata?: Record<string, any>,
    description?: string
  ): Promise<void> {
    return this.log({ event, userId, resourceType, resourceId, metadata, description });
  }
}

// Export singleton instance
export const activityLogger = new ActivityLoggerService();

// Export legacy function for backward compatibility
export async function logActivity(params: {
  actionType: string;
  actionCategory: string;
  description: string;
  userId?: string;
  resourceType?: string;
  resourceId?: string;
  metadata?: Record<string, any>;
}) {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      console.warn('Cannot log activity: no active session');
      return;
    }

    await supabase.functions.invoke('log-activity', {
      body: {
        ...params,
        actor_id: session.user.id,
      },
    });
  } catch (error) {
    console.error('Failed to log activity:', error);
  }
}
